/**
 * Draggable chip for CSV column order (wraps in a flex grid).
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { GripVertical } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { cn } from '@/lib/utils';

interface CsvExportSortableColumnRowProps {
  id: string;
  label: string;
  disabled: boolean;
}

export const CsvExportSortableColumnRow: FC<CsvExportSortableColumnRowProps> = ({
  id,
  label,
  disabled,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      role="listitem"
      className={cn(
        'flex max-w-[min(100%,11rem)] shrink-0 items-center gap-0.5 rounded-md border border-border/60 bg-background shadow-sm',
        isDragging && 'z-10 cursor-grabbing opacity-90 ring-2 ring-primary/25',
      )}
    >
      <button
        type="button"
        disabled={disabled}
        className={cn(
          'flex touch-none cursor-grab items-center justify-center rounded-l-md px-1.5 py-2 text-muted-foreground hover:bg-muted/80',
          disabled && 'pointer-events-none opacity-40',
          isDragging && 'cursor-grabbing',
        )}
        aria-label={`Drag to reorder ${label}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-3.5 w-3.5 shrink-0" aria-hidden />
      </button>
      <span className="truncate py-2 pr-2 text-xs font-medium leading-none">{label}</span>
    </div>
  );
};
